/**
 * Home hero: deferred mount of the mobile video.
 *
 * The <video> sits in an inert <template> and is cloned only below 750px, once the
 * poster image has loaded, so it never competes with it for bandwidth. On desktop, or
 * with prefers-reduced-motion, nothing is downloaded. There is no fallback branch: the
 * still image stays underneath until the video plays, and stays alone if it never does.
 * Only NotAllowedError is a refusal: other play() rejections are transient.
 * iOS autoplay needs `muted` and `playsinline`, set on the <video> in the template.
 */

const MOBILE = '(max-width: 749px)';
const REDUCED = '(prefers-reduced-motion: reduce)';

class MXHomeHero extends HTMLElement {
  connectedCallback() {
    this.template = this.querySelector('template[data-mx-hero-tpl]');
    if (!this.template) return;

    if (window.matchMedia(REDUCED).matches) return;

    const mq = window.matchMedia(MOBILE);
    if (mq.matches) {
      this.waitForPoster();
      return;
    }

    // Desktop to mobile (tablet rotation, resize): mount once, then stop listening.
    this.onCross = () => {
      if (!mq.matches) return;
      mq.removeEventListener('change', this.onCross);
      this.waitForPoster();
    };
    mq.addEventListener('change', this.onCross);
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }

  /**
   * Mounts the video once the poster has loaded. `complete` and the load/error events are
   * used rather than `img.decode()`, whose promise was seen to stay pending forever.
   * No timeout: a stalled poster must keep delaying the video.
   */
  waitForPoster() {
    const still = this.querySelector('img');
    if (!still || still.complete) {
      this.mount();
      return;
    }
    const go = () => this.mount();
    still.addEventListener('load', go, { once: true });
    still.addEventListener('error', go, { once: true });
  }

  mount() {
    if (this.video || !this.isConnected) return;

    const node = this.template.content.firstElementChild?.cloneNode(true);
    if (!node) return;

    this.appendChild(node);
    this.video = node;

    // Reveal before play(): Chrome pauses a muted video it considers invisible, and
    // opacity 0 counts. Until its first frame the video paints nothing over the poster.
    this.setAttribute('data-mx-video', '');

    this.play();

    // Pause off screen to save battery. No `root`: the viewport is used.
    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.play();
          else this.video?.pause();
        }
      });
      this.io.observe(this);
    }
  }

  play() {
    const promise = this.video?.play();
    if (!promise?.catch) return;
    promise.catch((err) => {
      if (err?.name === 'NotAllowedError') this.giveUp();
    });
  }

  /** Playback refused: remove the video, the poster stays. */
  giveUp() {
    this.io?.disconnect();
    this.io = null;
    this.video?.remove();
    this.video = null;
    this.removeAttribute('data-mx-video');
  }
}

if (!customElements.get('mx-home-hero')) {
  customElements.define('mx-home-hero', MXHomeHero);
}
